/**
 * The walk's checks (step 1.2 of docs/plans/setup-walk.md): each takes the
 * draft and returns what a person would meet using it. The structural checks
 * read only shape; the ones the cards mark `words` also read labels, and so
 * raise a `hint` at most (W7).
 *
 * Pure: the phone bundles it.
 */
import type { SetupDraftField, SetupDraftFormulaNode } from '../compose-author';
import type { SpecPostingRule, SpecRoleSource } from '../spec/posting';
import type { StudioDraft, StudioRecord } from '../setup-draft';
import { WALK_CARDS, type WalkCheckId, type WalkJourney, type WalkLevel } from './cards';
import { nodesOf, reachOfField, type ListRead, type Reach } from './reach';

export type WalkFinding = {
  /** `<check>:<record>[:<field>]`. */
  readonly id: string;
  readonly check: WalkCheckId;
  readonly level: WalkLevel;
  readonly journey: WalkJourney;
  readonly record: string;
  readonly field?: string;
  /** The list the finding reads through, and the field of it. */
  readonly list?: string;
  readonly listField?: string;
  /** Seeded rows the finding names, by their headline. */
  readonly rows?: readonly string[];
  /** One sentence for the team, with the draft's own names. */
  readonly says: string;
};

type Draft = Pick<StudioDraft, 'records'>;

const isMoney = (field: SetupDraftField): boolean => field.type === 'money' || (field.type === 'formula' && field.formula?.result === 'money');

/** Words that sell: a record by these names takes money for something. */
const SELLS = /\b(bookings?|reservations?|orders?|sales?|bills?|invoices?|memberships?|admissions?|appointments?|rentals?|subscriptions?|enrol+ments?)\b/i;
/** A price on a list. */
const PRICE = /\b(rates?|prices?|fees?|tariffs?|mrp|charges?|cost)\b/i;
/** A choice that sorts what is sold into kinds that may cost differently. */
const TYPE_LIKE = /\b(types?|category|categories|kinds?|class|plans?|packages?|tiers?|sizes?|variants?|grades?)\b/i;
/** Choices that sort something other than what is sold. */
const NOT_SOLD = /\b(id|identity|proof|documents?|payments?|pay|mode|status|source|gender|blood|channel|leads?)\b/i;
/** What is left to pay. */
const BALANCE = /\b(balance|dues?|owed?|outstanding|pending|remaining|baaki|baki|to\s+pay)\b/i;

/** Patterns that can leave money owed, and patterns that pay it. */
const OWES = new Set(['sale', 'purchase', 'charge', 'bill_lines']);
const PAYS = new Set(['receipt', 'payment', 'money_in', 'money_out', 'refund', 'concession']);

const wordsOf = (field: SetupDraftField): string => `${field.label} ${field.key.replace(/_/g, ' ')}`;
const recordOf = (draft: Draft, key: string | undefined): StudioRecord | undefined => draft.records.find((one) => one.key === key);
const isEntries = (record: StudioRecord): boolean => record.kind === 'record';

/** Every way a rule may post: its own pattern and roles alone, or under each choice. */
const postings = (rule: SpecPostingRule): Array<{ pattern: string; roles: Readonly<Record<string, SpecRoleSource>> }> => {
  const base = rule.roles ?? {};
  const options = Object.values(rule.byChoice?.options ?? {});
  return options.length === 0 ? [{ pattern: rule.pattern, roles: base }] : options.map((one) => ({ pattern: one.pattern ?? rule.pattern, roles: { ...base, ...(one.roles ?? {}) } }));
};
const roleSets = (rule: SpecPostingRule): Array<Readonly<Record<string, SpecRoleSource>>> => postings(rule).map((one) => one.roles);
const patternsOf = (rule: SpecPostingRule): string[] => [rule.pattern, ...postings(rule).map((one) => one.pattern)];

/** The field a role reads, when it reads one. */
const fieldOfRole = (source: SpecRoleSource | undefined): string | null => (source !== undefined && 'fieldKey' in source ? source.fieldKey : null);

/** Every formula tree a record's fields hold: worked out, defaults and the trees inside lines. */
const treesOf = (fields: readonly SetupDraftField[]): SetupDraftFormulaNode[] =>
  fields.flatMap((field) => [
    ...(field.formula === undefined ? [] : [field.formula.expr]),
    ...(field.startsWith === undefined ? [] : [field.startsWith.expr]),
    ...(field.lines === undefined ? [] : treesOf(field.lines.fields)),
  ]);

const nodesOfRecord = (record: StudioRecord): SetupDraftFormulaNode[] => treesOf(record.fields).flatMap(nodesOf);

const reads = (record: StudioRecord, key: string): boolean => nodesOfRecord(record).some((node) => node.kind === 'field' && node.fieldKey === key);

/** Whether a field's formula reads `key`, through other worked-out fields as well. */
const readsDeep = (record: StudioRecord, field: SetupDraftField, key: string, seen = new Set<string>()): boolean => {
  if (seen.has(field.key) || field.formula === undefined) return false;
  seen.add(field.key);
  return nodesOf(field.formula.expr).some((node) => {
    if (node.kind !== 'field') return false;
    if (node.fieldKey === key) return true;
    const next = record.fields.find((one) => one.key === node.fieldKey);
    return next !== undefined && readsDeep(record, next, key, seen);
  });
};

/** A seeded row's value, and its headline. */
const seedValue = (row: NonNullable<StudioRecord['seedRows']>[number], key: string): string | number | undefined => row.values.find((one) => one.fieldKey === key)?.value;
const rowName = (list: StudioRecord, row: NonNullable<StudioRecord['seedRows']>[number], at: number): string => String(seedValue(row, list.headlineFieldKey) ?? `row ${at + 1}`);
const emptyValue = (value: string | number | undefined): boolean => value === undefined || (typeof value === 'string' && value.trim() === '');

/** The field a money record's amount is: its rule's amount, else its money measure. */
const amountKeyOf = (record: StudioRecord): string | null => {
  const rule = record.posting;
  if (rule !== undefined) {
    if (rule.pattern === 'bill_lines') return null;
    const key = fieldOfRole(rule.roles?.amount);
    if (key !== null) return key;
  }
  const measure = record.fields.find((field) => field.key === record.measureFieldKey);
  return (measure !== undefined && isMoney(measure) ? measure : record.fields.find(isMoney))?.key ?? null;
};

/** Every money record with an amount to reach, and where it reaches from. */
const amounts = (draft: Draft): Array<{ record: StudioRecord; key: string; reach: Reach }> =>
  draft.records.flatMap((record) => {
    // A record that moves no money (a membership billed monthly by its plan instead) has no amount to reach.
    if (!isEntries(record) || (record.posting === undefined && record.direction === 'none')) return [];
    const key = amountKeyOf(record);
    if (key === null) return [];
    return [{ record, key, reach: reachOfField(draft, record, key) }];
  });

const finding = (check: WalkCheckId, record: StudioRecord, says: string, over: Partial<WalkFinding> = {}): WalkFinding => ({
  id: [check, record.key, over.field].filter((one) => one !== undefined).join(':'),
  check,
  level: WALK_CARDS[check].level,
  journey: WALK_CARDS[check].journey,
  record: record.key,
  says,
  ...over,
});

/** The person's word for one entry, with its article: "a booking", "an order"; a bare "entry" says the record's name. */
const noun = (record: StudioRecord): string => {
  const word = record.entryNoun.trim() === '' || /^entr(y|ies)$/i.test(record.entryNoun.trim()) ? `${record.name} entry` : record.entryNoun;
  return `${/^[aeiou]/i.test(word) ? 'an' : 'a'} ${word}`;
};
/** Rows by name, the first five and a count of the rest. */
const named = (rows: readonly string[]): string => (rows.length <= 5 ? rows.join(', ') : `${rows.slice(0, 5).join(', ')} and ${rows.length - 5} more`);
const labelOf = (record: StudioRecord, key: string): string => record.fields.find((one) => one.key === key)?.label ?? key;

/* ------------------------------------------------------------------ take */

export const amountUnreachable = (draft: Draft): WalkFinding[] =>
  amounts(draft)
    .filter(({ record, reach }) => reach.never.length > 0 && record.posting !== undefined)
    .map(({ record, key, reach }) =>
      finding('amount_unreachable', record, `Take ${noun(record)}: its ${labelOf(record, key)} can never be worked out, because ${reach.never[0]}.`, { field: key }),
    );

export const noPrice = (draft: Draft): WalkFinding[] =>
  draft.records.flatMap((record) => {
    if (!isEntries(record) || record.posting !== undefined) return [];
    if (record.fields.some(isMoney) || record.fields.some((field) => field.lines?.fields.some(isMoney) === true)) return [];
    if (!SELLS.test(`${record.name} ${record.entryNoun}`)) return [];
    // A record of payments that picks this one keeps its money for it.
    const kept = draft.records.some(
      (other) => other.key !== record.key && isEntries(other) && (other.posting !== undefined || other.fields.some(isMoney)) && other.fields.some((field) => field.targetRecordKey === record.key),
    );
    if (kept) return [];
    return [finding('no_price', record, `Take ${noun(record)}: ${record.name} holds no price, total or amount, and no money record picks it.`)];
  });

/** The rows of `read.list` that do not hold `read.field`, by name. */
const rowsWithout = (draft: Draft, read: ListRead): { list: StudioRecord; field: SetupDraftField; rows: string[]; seeded: number } | null => {
  const list = recordOf(draft, read.list);
  const field = list?.fields.find((one) => one.key === read.field);
  if (list === undefined || field === undefined) return null;
  const seeds = list.seedRows ?? [];
  return { list, field, rows: seeds.flatMap((row, at) => (emptyValue(seedValue(row, read.field)) ? [rowName(list, row, at)] : [])), seeded: seeds.length };
};

const onceEach = (reads: readonly ListRead[]): ListRead[] => [...new Map(reads.map((one) => [`${one.list}.${one.field}`, one])).values()];

export const priceFromList = (draft: Draft): WalkFinding[] =>
  amounts(draft).flatMap(({ record, key, reach }) => {
    if (reach.never.length > 0) return [];
    const out: WalkFinding[] = [];
    for (const read of onceEach(reach.fromList)) {
      const held = rowsWithout(draft, read);
      if (held === null) continue;
      const at = { field: key, list: held.list.key, listField: held.field.key };
      if (held.rows.length > 0) {
        out.push(
          finding('price_from_list', record, `Take ${noun(record)}: its ${labelOf(record, key)} needs ${held.field.label} from ${held.list.name}, and ${named(held.rows)} hold none, so an entry that picks one has no amount.`, { ...at, rows: held.rows }),
        );
      } else if (held.field.required !== true) {
        out.push(
          finding('price_from_list', record, `Take ${noun(record)}: its ${labelOf(record, key)} needs ${held.field.label} from ${held.list.name}, which a row may leave empty.`, { ...at, level: 'hint' }),
        );
      }
    }
    for (const read of onceEach(reach.startsFrom)) {
      const held = rowsWithout(draft, read);
      if (held === null || held.rows.length === 0) continue;
      out.push(
        finding('price_from_list', record, `Take ${noun(record)}: its price starts from ${held.field.label} on ${held.list.name}, and ${named(held.rows)} hold none, so it opens empty there.`, {
          field: key,
          list: held.list.key,
          listField: held.field.key,
          rows: held.rows,
          level: 'hint',
        }),
      );
    }
    return out;
  });

/** Whether a record takes money for something: a sale, a charge or a bill of lines, under any choice. */
const sells = (record: StudioRecord): boolean => record.posting !== undefined && patternsOf(record.posting).some((one) => one === 'sale' || one === 'charge' || one === 'bill_lines');
/** Whether it prices something bought or sold, never a payment of what is owed. */
const prices = (record: StudioRecord): boolean => record.posting !== undefined && patternsOf(record.posting).some((one) => OWES.has(one));
/** The pick its money rule names as who owes: its list sorts people, never what is sold. */
const partyPick = (record: StudioRecord): string | null => (record.posting === undefined ? null : roleSets(record.posting).map((roles) => fieldOfRole(roles.party)).find((one) => one !== null) ?? null);

export const priceTypedEachTime = (draft: Draft): WalkFinding[] =>
  amounts(draft).flatMap(({ record, key, reach }) => {
    if (!prices(record) || record.fields.find((one) => one.key === key)?.type === 'rollup') return [];
    const out: WalkFinding[] = [];
    const hops = nodesOfRecord(record).filter((node) => node.kind === 'hop');
    for (const pick of record.fields) {
      if (pick.type !== 'reference') continue;
      const list = recordOf(draft, pick.targetRecordKey);
      if (list === undefined || list.kind !== 'directory' || list.accounts !== undefined) continue;
      const price = list.fields.find((one) => (one.type === 'number' || one.type === 'money' || one.type === 'formula') && PRICE.test(wordsOf(one)));
      if (price === undefined) continue;
      const used = hops.some((node) => node.kind === 'hop' && node.via === pick.key) || [...reach.fromList, ...reach.startsFrom].some((one) => one.via === pick.key);
      if (used) continue;
      out.push(finding('price_typed_each_time', record, `Take ${noun(record)}: ${list.name} holds ${price.label}, and ${labelOf(record, key)} never starts from it, so it is typed every time.`, { field: key, list: list.key }));
    }
    return out;
  });

/** A choice of kinds that may cost differently: never a choice of direction (Cash in, Cash out), which signs the money instead. */
const typeLike = (field: SetupDraftField): boolean =>
  field.type === 'select' && (field.options?.length ?? 0) >= 2 && TYPE_LIKE.test(wordsOf(field)) && !NOT_SOLD.test(wordsOf(field)) && !(field.options ?? []).some((one) => one.sign !== undefined);

export const choiceWithoutPrice = (draft: Draft): WalkFinding[] =>
  amounts(draft).flatMap(({ record, key, reach }) => {
    if (!sells(record)) return [];
    const out: WalkFinding[] = [];
    const party = partyPick(record);
    const options = (field: SetupDraftField): string => (field.options ?? []).map((one) => one.label).join(', ');
    for (const field of record.fields.filter(typeLike)) {
      if (reads(record, field.key) || record.posting?.byChoice?.fieldKey === field.key || record.signFieldKey === field.key) continue;
      out.push(finding('choice_without_price', record, `Take ${noun(record)}: ${field.label} (${options(field)}) is chosen on it, and no price follows the choice.`, { field: field.key }));
    }
    const listed = new Set([...reach.fromList, ...reach.startsFrom].map((one) => one.list));
    for (const pick of record.fields) {
      if (pick.type !== 'reference' || pick.key === party) continue;
      const list = recordOf(draft, pick.targetRecordKey);
      // The Accounts list's Type sorts accounts, never what is sold.
      if (list === undefined || list.kind !== 'directory' || list.accounts !== undefined || listed.has(list.key)) continue;
      const hopped = nodesOfRecord(record).some((node) => node.kind === 'hop' && node.via === pick.key);
      if (hopped) continue;
      for (const field of list.fields.filter(typeLike)) {
        out.push(
          finding('choice_without_price', record, `Take ${noun(record)}: each ${list.entryNoun || list.name} has a ${field.label} (${options(field)}), and the ${labelOf(record, key)} does not follow it.`, {
            field: key,
            list: list.key,
          }),
        );
      }
    }
    return out;
  });

/* ---------------------------------------------------------------- settle */


/**
 * Whether the amount is the sum of the parts paid at once (a day's Cash
 * amount and UPI amount, `modePartsOps`): paid in full there and then, never
 * owed. Needs the record to read the amount's formula.
 */
const paidInParts = (roles: Readonly<Record<string, SpecRoleSource>>, record: StudioRecord | undefined): boolean => {
  const amount = fieldOfRole(roles.amount);
  const sum = record?.fields.find((field) => field.key === amount)?.formula?.expr;
  if (sum === undefined || roles.paid_now_2 === undefined) return false;
  const parts = ['paid_now', 'paid_now_2', 'paid_now_3'].map((role) => fieldOfRole(roles[role])).filter((one): one is string => one !== null);
  return [...summed(sum)].sort().join(' ') === parts.sort().join(' ');
};

/** Whether some way a rule posts leaves part of it owed: no place it is paid to, or only a part paid now. */
const canOwe = (rule: SpecPostingRule, record?: StudioRecord): boolean =>
  postings(rule).some(({ pattern, roles }) => {
    if (pattern === 'charge') return true;
    if (!OWES.has(pattern) || paidInParts(roles, record)) return false;
    const paidAt = pattern === 'purchase' ? (roles.paid_from ?? roles.paid_to) : roles.paid_to;
    return paidAt === undefined || roles.paid_now !== undefined;
  });

/**
 * Whether the rule SAYS part of it may be owed: a part paid now, a choice
 * that puts it on credit, or a charge. A rule that says nothing of how it is
 * paid is asked how (`sale_without_payment`) before code adds what pays its
 * dues; only then is an owing the person meant.
 */
export const owesExplicitly = (rule: SpecPostingRule, record?: StudioRecord): boolean =>
  postings(rule).some(({ pattern, roles }) => {
    if (pattern === 'charge') return true;
    if (!OWES.has(pattern) || paidInParts(roles, record)) return false;
    const paidAt = pattern === 'purchase' ? (roles.paid_from ?? roles.paid_to) : roles.paid_to;
    const onCredit = paidAt !== undefined && 'options' in paidAt && Object.values(paidAt.options ?? {}).some((one) => 'systemKey' in one && (one.systemKey === 'receivable' || one.systemKey === 'payable'));
    return roles.paid_now !== undefined || onCredit;
  }) || (Object.keys(rule.byChoice?.options ?? {}).length > 1 && postings(rule).some(({ roles }) => roles.paid_to === undefined) && postings(rule).some(({ roles }) => roles.paid_to !== undefined));

/** Who a rule's party is, as one key: the record an entry is its own party of, or the list or record its party picks. */
export const partyOf = (record: StudioRecord, rule: SpecPostingRule): string | null => {
  for (const roles of [rule.roles ?? {}, ...roleSets(rule)]) {
    const party = roles.party;
    if (party === undefined) continue;
    if ('self' in party) return record.key;
    const key = fieldOfRole(party);
    const pick = key === null ? undefined : record.fields.find((one) => one.key === key);
    if (pick?.targetRecordKey !== undefined) return pick.targetRecordKey;
  }
  return null;
};

export const duesWithNoParty = (draft: Draft): WalkFinding[] =>
  draft.records.flatMap((record) => {
    const rule = record.posting;
    if (!isEntries(record) || rule === undefined || !patternsOf(rule).some((one) => OWES.has(one)) || !canOwe(rule, record)) return [];
    const named = [rule.roles ?? {}, ...roleSets(rule)].some((roles) => roles.party !== undefined);
    if (named) return [];
    return [finding('dues_with_no_party', record, `Settle ${noun(record)}: what is not paid at once is owed, and the money rule names nobody who owes it.`)];
  });

/** Every party something pays: a receipt or payment that picks it, or payment lines on an entry that owes it. */
export const settledParties = (draft: Draft): Set<string> => {
  const settled = new Set<string>();
  for (const record of draft.records) {
    const rule = record.posting;
    if (!isEntries(record) || rule === undefined) continue;
    const party = partyOf(record, rule);
    if (party === null) continue;
    // Payment lines settle only an entry that is its own party (a booking); a party picked from a list is settled by a
    // record of its payments, one payment for many bills (D1, owner 31 Oct).
    const linesPay = party === record.key && roleSets(rule).some((roles) => roles.payments !== undefined);
    if (linesPay || patternsOf(rule).some((one) => PAYS.has(one))) settled.add(party);
  }
  return settled;
};

export const noSettlePath = (draft: Draft): WalkFinding[] => {
  const settled = settledParties(draft);
  return draft.records.flatMap((record) => {
    const rule = record.posting;
    if (!isEntries(record) || rule === undefined || !patternsOf(rule).some((one) => OWES.has(one)) || !canOwe(rule, record)) return [];
    const party = partyOf(record, rule);
    if (party === null || settled.has(party)) return [];
    const who = recordOf(draft, party);
    const whom = party === record.key ? `${noun(record)} itself` : `${who === undefined ? 'a party' : noun(who)} of ${who?.name ?? party}`;
    return [finding('no_settle_path', record, `Settle ${noun(record)}: ${whom} can owe, and no record or payment lines take the rest.`)];
  });
};

/* ----------------------------------------------------------------- count */

export const linesMoneyUncounted = (draft: Draft): WalkFinding[] =>
  draft.records.flatMap((record) => {
    if (!isEntries(record)) return [];
    const aggs = nodesOfRecord(record).filter((node) => node.kind === 'agg');
    const roled = new Set(record.posting === undefined ? [] : roleSets(record.posting).flatMap((roles) => Object.values(roles).map(fieldOfRole)));
    return record.fields.flatMap((field) => {
      if (field.type !== 'lines' || field.lines?.fields.some(isMoney) !== true) return [];
      if (aggs.some((node) => node.kind === 'agg' && node.lines === field.key) || roled.has(field.key)) return [];
      return [finding('lines_money_uncounted', record, `Count ${noun(record)}: the ${field.label} lines hold money that no total and no money rule adds up.`, { field: field.key })];
    });
  });

export const rollupNotRead = (draft: Draft): WalkFinding[] =>
  draft.records.flatMap((record) => {
    if (!isEntries(record)) return [];
    const balance = record.fields.find((field) => field.type === 'formula' && BALANCE.test(wordsOf(field)));
    if (balance === undefined) return [];
    return record.fields.flatMap((field) => {
      if (field.type !== 'rollup' || field.rollup?.agg !== 'sum' || readsDeep(record, balance, field.key)) return [];
      return [finding('rollup_not_read', record, `Settle ${noun(record)}: ${field.label} adds up other entries, and ${balance.label} leaves it out.`, { field: field.key })];
    });
  });

/** What was paid at once, by a bill's words: Paid now, Advance, Jama, Dibayar. */
const PAID_WORDS = /\b(paid|received|advance|jama|paid now|deposit|dibayar|terbayar|uang muka|down payment|dp)\b/i;

/** The fields a sum (`a + b`, `a or 0`) reads, never through a total of lines. */
const summed = (node: SetupDraftFormulaNode): string[] =>
  node.kind === 'field' ? [node.fieldKey] : node.kind === 'calc' && node.op === 'add' ? [...summed(node.left), ...summed(node.right)] : node.kind === 'coalesce' ? node.of.flatMap(summed) : [];

/**
 * A bill that keeps what was paid at once in a field its money rule never
 * reads (M2): a Balance that takes it from the amount says so by shape, and
 * blocks; a name alone (Paid now, Dibayar) is a hint (W7).
 */
export const paidNotCounted = (draft: Draft): WalkFinding[] =>
  draft.records.flatMap((record) => {
    const rule = record.posting;
    if (!isEntries(record) || rule === undefined || !patternsOf(rule).some((one) => one === 'sale' || one === 'purchase')) return [];
    const read = new Set([...roleSets(rule).flatMap((roles) => Object.values(roles).map(fieldOfRole)), fieldOfRole(rule.roles?.amount)].filter((one): one is string => one !== null));
    const amount = amountKeyOf(record);
    if (amount === null) return [];
    const taken = new Set(
      nodesOfRecord(record).flatMap((node) => (node.kind === 'calc' && node.op === 'subtract' && summed(node.left).includes(amount) ? summed(node.right) : [])),
    );
    // A purchase's Balance may take what was spent or returned (Balance with driver): only a sale's says paid by shape.
    const sale = patternsOf(rule).includes('sale');
    return record.fields.flatMap((field) => {
      if (field.type !== 'money' || field.key === amount || read.has(field.key)) return [];
      const byShape = sale && taken.has(field.key);
      if (!byShape && !PAID_WORDS.test(field.label)) return [];
      return [
        finding('paid_not_counted', record, `Count ${noun(record)}: ${field.label} says what was paid at once, and the money rule never reads it, so a part payment counts as all or nothing.`, {
          field: field.key,
          ...(byShape ? {} : { level: 'hint' as const }),
        }),
      ];
    });
  });

export const WALK_CHECKS: Readonly<Record<WalkCheckId, (draft: Draft) => WalkFinding[]>> = {
  amount_unreachable: amountUnreachable,
  no_price: noPrice,
  price_from_list: priceFromList,
  price_typed_each_time: priceTypedEachTime,
  choice_without_price: choiceWithoutPrice,
  dues_with_no_party: duesWithNoParty,
  no_settle_path: noSettlePath,
  lines_money_uncounted: linesMoneyUncounted,
  rollup_not_read: rollupNotRead,
  paid_not_counted: paidNotCounted,
};
